import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useDisclosure } from '@reactuses/core';
import { motion } from 'framer-motion';
import { DefaultPopup } from './popups/DefaultPopup';
import { Button } from './ui/button';
import { formatTime_MMSS } from '@/lib/utils/dateUtils';
import {
  gameDataContext,
  getGameIndexByPath,
} from '@/providers/GameDataProvider';
import { Diamond } from './Diamond';

type Props = {
  open: boolean;
  timeInSec: number;
  showTimer?: boolean;
  onSkipButtonClick?: () => void;
  onSkipConfirm?: () => void;
  onSkipCancel?: () => void;
};

export const GameHeader = ({
  open,
  timeInSec,
  showTimer = true,
  onSkipButtonClick,
  onSkipConfirm,
  onSkipCancel,
}: Props) => {
  const {
    openProgressPopup,
    // totalScore,
    currentGameScore,
    setCurrentGameIndex,
    nextGamePath,
  } = useContext(gameDataContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentGameIndex = getGameIndexByPath(window.location.pathname);

  // skip popup
  const {
    isOpen: skipPopupOpen,
    onClose: close_skipPopup,
    onOpen: open_skipPopup,
  } = useDisclosure();

  // --------------------- methods ------------------------

  const handleSkip = () => {
    // Use the provided callback if available, otherwise navigate directly
    if (onSkipButtonClick) {
      open_skipPopup();
      onSkipButtonClick?.();
    } else {
      handleSkipConfirm();
    }
  };

  const handleSkipConfirm = () => {
    console.log('handleSkipConfirm');
    close_skipPopup();
    onSkipConfirm?.();
    if (!nextGamePath) {
      console.error(
        'nextGamePath not found for skip, this should never happen'
      );
      return; // this should never happen
    }
    navigate(nextGamePath);
  };

  const handleSkipCancel = () => {
    close_skipPopup();
    onSkipCancel?.();
  };

  // --------------------- effects ------------------------

  // each time the current game index changes (from path), we update the current game index on the context
  useEffect(() => {
    console.log('currentGameIndex changed', currentGameIndex);
    setCurrentGameIndex(currentGameIndex);
  }, [currentGameIndex, setCurrentGameIndex]);

  // --------------------- render ------------------------
  return (
    <>
      <motion.div
        className="fixed left-0 top-0 z-40 w-full bg-primary drop-shadow-lg"
        initial={{ y: -200 }}
        animate={{ y: open ? 0 : -200 }}
        transition={{ duration: 1, easings: 'easeInOut' }}
      >
        <header className="relative z-40 flex min-h-12 w-full justify-between bg-primary">
          {/* total score */}
          {/* <div className="absolute left-4 top-16 flex cursor-pointer justify-center">
            <Diamond
              value={totalScore}
              className="h-10"
              onClick={() => openProgressPopup()}
            />
          </div> */}
          {/* current game score */}
          <div className="absolute left-1/4 top-4 flex w-1/2 cursor-pointer justify-center">
            <Diamond
              value={currentGameScore}
              className="h-20"
              onClick={() => openProgressPopup()}
            />
          </div>

          {showTimer && (
            <div className="absolute right-4 top-1 items-center text-4xl font-bold">
              {timeInSec ? formatTime_MMSS(timeInSec) : '00:00'}
            </div>
          )}
        </header>

        {
          // we create a skip button that goes to the next game, if we are in a game!
          nextGamePath && (
            <div className="p-y-4 absolute right-0 top-16 overflow-hidden px-4">
              <motion.div
                initial={{ x: 200, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{
                  duration: 0.5,
                  ease: 'easeOut',
                  delay: 1, // 1 second delay after the main animation
                }}
              >
                <Button
                  variant="ghost"
                  size="xs"
                  animation="pulse"
                  onClick={handleSkip}
                >
                  {t('common.skip')}
                </Button>
              </motion.div>
            </div>
          )
        }
      </motion.div>

      {/* Skip Confirmation Popup */}
      <DefaultPopup
        isOpen={skipPopupOpen}
        title={t('popup.skip.title')}
        description={t('popup.skip.description')}
        buttonLabel={t('popup.skip.button')}
        cancelLabel={t('common.cancel')}
        onClose={handleSkipCancel}
        onButtonClick={handleSkipConfirm}
      />
    </>
  );
};
